import { createContext, useContext, useId, type ComponentProps, type ReactNode } from 'react';
import { cn } from './cn';
import { focusRing } from './focus';

type FieldState = { id: string; describedBy: string | undefined; invalid: boolean };
const FieldContext = createContext<FieldState | null>(null);

/** A labelled control: the input inside gets its id, `aria-invalid` and `aria-describedby` (error, then hint). */
export function Field({ label, error, hint, className, children }: { label: ReactNode; error?: string | null; hint?: ReactNode; className?: string; children: ReactNode }) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: !!error }}>
      <div className={cn('flex flex-col gap-1.5 font-sans', className)}>
        <label htmlFor={id} className="text-sm font-medium text-fg">
          {label}
        </label>
        {children}
        {error && (
          <p id={errorId} className="text-xs text-danger">
            {error}
          </p>
        )}
        {hint && (
          <p id={hintId} className="text-xs text-fg-muted">
            {hint}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

const control = cn(
  'w-full rounded-xl border border-border-strong bg-surface px-3 font-sans text-sm text-fg placeholder:text-fg-subtle transition-colors duration-(--duration-fast) hover:border-fg-muted disabled:opacity-50 aria-invalid:border-danger',
  focusRing,
);

function useField() {
  const f = useContext(FieldContext);
  return f ? { id: f.id, 'aria-describedby': f.describedBy, 'aria-invalid': f.invalid || undefined } : {};
}

/**
 * Props include `ref` (React 19), for callers that focus or select the field. `bare`: a field set in a bar of its own
 * (the command palette's search), without border or fill; the bar around it frames it.
 */
export function Input({ className, bare, ...rest }: ComponentProps<'input'> & { bare?: boolean }) {
  return (
    <input
      {...useField()}
      className={cn(control, 'h-10 rounded-full px-4 pointer-coarse:h-11', bare && 'h-12 rounded-none border-0 bg-transparent px-0 text-base hover:border-0 focus-visible:outline-none pointer-coarse:h-12', className)}
      {...rest}
    />
  );
}

export function Textarea({ className, ...rest }: ComponentProps<'textarea'>) {
  return <textarea {...useField()} className={cn(control, 'min-h-20 py-2', className)} {...rest} />;
}

export function Select({ className, ...rest }: ComponentProps<'select'>) {
  return <select {...useField()} className={cn(control, 'h-10 rounded-full pl-4 pr-8 pointer-coarse:h-11', className)} {...rest} />;
}
